import bwipjs from 'bwip-js';
import { CardTemplate } from '../types';

const imageCache: Record<string, HTMLImageElement> = {};

const convertGoogleDriveUrl = (url: string): string => {
  if (!url || typeof url !== 'string') return url;
  let trimmed = url.trim();
  if (trimmed.includes('drive.google.com') && !trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    trimmed = 'https://' + trimmed;
  }
  if (trimmed.includes('drive.google.com') || trimmed.includes('docs.google.com')) {
    let fileId = '';
    const fileDMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (fileDMatch && fileDMatch[1]) {
      fileId = fileDMatch[1];
    } else {
      const idMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch && idMatch[1]) {
        fileId = idMatch[1];
      }
    }
    if (fileId) {
      const directUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w800`;
      return `https://images1-focus-opensocial.googleusercontent.com/gadgets/proxy?container=focus&refresh=2592000&url=${encodeURIComponent(directUrl)}`;
    }
  }
  return trimmed;
};

const loadImage = (src: string, isCrossorigin = false): Promise<HTMLImageElement> => {
  const convertedSrc = convertGoogleDriveUrl(src);
  if (imageCache[convertedSrc]) {
    return Promise.resolve(imageCache[convertedSrc]);
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const isGoogleDrive = convertedSrc.includes('googleusercontent.com') || convertedSrc.includes('drive.google.com');
    if (isCrossorigin || isGoogleDrive) {
      img.crossOrigin = 'Anonymous';
    }
    img.onload = () => {
      imageCache[convertedSrc] = img;
      resolve(img);
    };
    img.onerror = (e) => reject(e);
    img.src = convertedSrc;
  });
};

export const formatSerialNoForDisplay = (val: any): string => {
  if (!val) return '';
  const strVal = String(val);
  if (strVal.toLowerCase().includes('draft')) {
    const match = strVal.match(/^([A-Z]+)-DRAFT-(\d+)$/i);
    if (match) {
      const category = match[1].toUpperCase();
      const num = parseInt(match[2], 10);
      return `${category}-${num.toString().padStart(4, '0')}`;
    }
    const matchSimple = strVal.match(/^DRAFT-(\d+)$/i);
    if (matchSimple) {
      const num = parseInt(matchSimple[1], 10);
      return `DRAFT-${num.toString().padStart(4, '0')}`;
    }
  }
  return strVal;
};

export const drawCardOnCanvas = async (
  canvas: HTMLCanvasElement, 
  template: CardTemplate, 
  data: Record<string, any>
): Promise<void> => {
  const ctx = canvas.getContext('2d');
  if (!ctx || !template.backgroundImage) return;

  try {
    // 1. Reset Canvas State for each draw
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.setLineDash([]);
    
    // Load Background Image
    const bgImg = await loadImage(template.backgroundImage, true);
    
    // Standardize canvas size or use image size
    canvas.width = bgImg.width > 0 ? bgImg.width : 1011; // Approx credit card ratio high res
    canvas.height = bgImg.height > 0 ? bgImg.height : 638;
    
    // Draw Background
    ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);

    // Create a copy of the data and format the serial numbers for card presentation
    const presentedData = { ...data };
    const keysToFormat = ['serialNo', 'idNo', 'id_no', 'id'];
    for (const key of keysToFormat) {
      if (presentedData[key]) {
        presentedData[key] = formatSerialNoForDisplay(presentedData[key]);
      }
    }

    // 2. Draw Fields
    for (const field of template.fields) {
      let value = presentedData[field.key];
      
      // Fallback for common field mappings
      if (!value) {
        if (field.key === 'headName' || field.key === 'name' || field.key === 'studentName') {
          value = presentedData['name'] || presentedData['studentName'] || presentedData['headName'] || presentedData['fullName'];
        }
        if (field.key === 'housePhoto' || field.key === 'photo' || field.key === 'image') {
          value = presentedData['housePhoto'] || presentedData['photo'] || presentedData['image'] || presentedData['studentPhoto'] || presentedData['memberPhoto'];
        }
        if (field.key === 'phone') {
          value = presentedData['phone'] || presentedData['mobile'] || presentedData['contact'];
        }
      }
      
      if (field.key === 'barcode') {
        try {
          // Generate Barcode (CODE128) containing the serial number
          const serialNo = String(presentedData['serialNo'] || '');
          if (serialNo) {
            const barcodeCanvas = document.createElement('canvas');
            await bwipjs.toCanvas(barcodeCanvas, {
              bcid: 'code128',       // Barcode type
              text: serialNo,        // Text to encode
              scale: 3,              // 3x scaling factor
              height: 10,            // Bar height, in millimeters
              includetext: true,     // Show human-readable text
              textxalign: 'center',  // Always good to set this
            });

            const size = field.fontSize > 30 ? field.fontSize : 120;
            // Calculate height to maintain barcode aspect ratio (approx 1:3)
            const barcodeHeight = size * 0.4; 
            ctx.drawImage(barcodeCanvas, field.x, field.y - barcodeHeight, size * 2, barcodeHeight);
          }
        } catch (barcodeError) {
          console.error("Failed to draw barcode", barcodeError);
        }
      } else if (field.key === 'qrCode') {
        // The user specifically asked to make QR code linear code 128
        try {
          const serialNo = String(presentedData['serialNo'] || '');
          if (serialNo) {
            const barcodeCanvas = document.createElement('canvas');
            await bwipjs.toCanvas(barcodeCanvas, {
              bcid: 'code128',
              text: serialNo,
              scale: 3,
              height: 10,
              includetext: true,
              textxalign: 'center',
            });

            const size = field.fontSize > 30 ? field.fontSize : 100;
            const barcodeHeight = size * 0.4; 
            ctx.drawImage(barcodeCanvas, field.x, field.y - barcodeHeight, size * 2, barcodeHeight);
          }
        } catch (qrError) {
          console.error("Failed to draw barcode for QR legacy field", qrError);
        }
      } else if (field.key === 'housePhoto' || (typeof value === 'string' && (value.startsWith('data:image') || value.startsWith('http')))) {
        // Handled below if it's the specific photoConfig, or here if it's a field
        if (!template.photoConfig || field.key !== 'housePhoto') {
          try {
            const photoUrl = String(value || '');
            if (photoUrl) {
              const isDataUrl = photoUrl.startsWith('data:image');
              const photoImg = await loadImage(photoUrl, !isDataUrl);
              const targetWidth = field.fontSize > 30 ? field.fontSize : 150;
              const targetHeight = photoImg.height * (targetWidth / photoImg.width);
              ctx.drawImage(photoImg, field.x, field.y, targetWidth, targetHeight);
            }
          } catch (photoError) {
            console.error("Failed to load field image", photoError);
          }
        }
      } else {
        // Normal Text Drawing
        ctx.font = `bold ${field.fontSize}px ${field.fontFamily || 'Arial'}`;
        ctx.fillStyle = field.color;
        
        // Handle alignment
        if (field.alignment) {
          ctx.textAlign = field.alignment;
        } else {
          ctx.textAlign = 'left';
        }
        
        let displayValue = String(value || '');
        if (!displayValue && data.isEditor) {
          displayValue = `[${field.label}]`;
          ctx.fillStyle = 'rgba(100, 100, 100, 0.5)'; // Faint gray for missing data in editor
        }
        
        ctx.fillText(displayValue, field.x, field.y);
      }
    }

    // 3. Draw Photo Box if configured (Priority over housePhoto field)
    if (template.photoConfig) {
      const photoUrl = presentedData['housePhoto'] || presentedData['photo'] || presentedData['image'] || presentedData['studentPhoto'] || presentedData['memberPhoto'] || presentedData['house_photo'];
      
      // If we are in "Editor Mode" (no photo data), draw a placeholder box
      if (!photoUrl && (data.isEditor || presentedData['id']?.includes('[Template]'))) {
        ctx.strokeStyle = '#10b981'; // Emerald 500
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(template.photoConfig.x, template.photoConfig.y, template.photoConfig.width, template.photoConfig.height);
        
        ctx.fillStyle = '#10b981';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('PHOTO', template.photoConfig.x + (template.photoConfig.width / 2), template.photoConfig.y + (template.photoConfig.height / 2));
        ctx.setLineDash([]);
      } else if (photoUrl && typeof photoUrl === 'string') {
        try {
          const isDataUrl = photoUrl.startsWith('data:image');
          const photoImg = await loadImage(photoUrl, !isDataUrl);
          
          const targetX = template.photoConfig.x;
          const targetY = template.photoConfig.y;
          const targetW = template.photoConfig.width;
          const targetH = template.photoConfig.height;

          // CENTER CROP LOGIC
          const imgAspect = photoImg.width / photoImg.height;
          const targetAspect = targetW / targetH;

          let sourceX = 0;
          let sourceY = 0;
          let sourceW = photoImg.width;
          let sourceH = photoImg.height;

          if (imgAspect > targetAspect) {
            // Image is wider than target aspect ratio - crop sides
            sourceW = photoImg.height * targetAspect;
            sourceX = (photoImg.width - sourceW) / 2;
          } else {
            // Image is taller than target aspect ratio - crop top/bottom
            sourceH = photoImg.width / targetAspect;
            sourceY = (photoImg.height - sourceH) / 2;
          }

          ctx.drawImage(
            photoImg, 
            sourceX, sourceY, sourceW, sourceH, // Source
            targetX, targetY, targetW, targetH  // Destination
          );

          // In editor mode, draw a helper border so they can see the cropping boundary
          if (data.isEditor) {
            ctx.strokeStyle = 'rgba(16, 185, 129, 0.5)'; // Faint Emerald
            ctx.lineWidth = 1;
            ctx.setLineDash([2, 2]);
            ctx.strokeRect(targetX, targetY, targetW, targetH);
            ctx.setLineDash([]);
          }
        } catch (photoError) {
          console.error("Failed to draw configured photo box", photoError);
        }
      }
    }
  } catch (e) {
    console.error("Failed to load and draw card template image", e);
  }
};

export const downloadCanvas = (canvas: HTMLCanvasElement, filename: string) => {
  try {
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (e) {
    console.error("Download failed, likely due to CORS:", e);
    alert("Cannot download: Security error. Please ensure the template image is loaded correctly.");
  }
};

export const AVAILABLE_FIELDS = [
  { key: 'serialNo', label: 'ക്രമനമ്പർ (Serial No)' },
  { key: 'headName', label: 'പേര് (Name)' },
  { key: 'houseName', label: 'വീട്ട് പേര് (House Name)' },
  { key: 'wardNo', label: 'വാർഡ് (Ward)' },
  { key: 'phone', label: 'ഫോൺ (Phone)' },
  { key: 'categoryName', label: 'കാറ്റഗറി (Category)' },
  { key: 'housePhoto', label: 'ഫോട്ടോ (Photo)' },
  { key: 'barcode', label: 'ബാർകോഡ് (Barcode - Code128)' },
  { key: 'qrCode', label: 'ലീനിയർ കോഡ് (Linear Code 128)' },
];